// WebBleStatusBadge — indicador GLOBAL (en cualquier página) del estado de la impresora
// Bluetooth. Resuelve dos requisitos:
//
// 1. Aviso accionable: si hay alguna impresora emparejada+habilitada SIN conexión, el
//    usuario debe enterarse antes de intentar imprimir y fallar sin saber por qué.
//    Esto cubre el caso multi-impresora (p.ej. Caja conectada + Cocina caída): mientras
//    falte aunque sea una, mostramos la barra "por confirmar".
//
// 2. Confirmación positiva: cuando TODAS las emparejadas+habilitadas están conectadas,
//    mostramos una píldora verde sutil ("Impresora lista") para que el cajero en Pedidos
//    sepa de un vistazo que puede imprimir. Discreta, no una barra de alarma.
//
// En 'off'/'unsupported' no se muestra nada. Tocar el aviso lleva a Ajustes › Impresión.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebBleReady } from '@utils/hooks/useWebBleReady';

const PRINTING_DEEP_LINK = '/ajustes?section=printing';

// Flag de sesión: una vez descartada la barra "por confirmar", no vuelve a aparecer
// hasta recargar la página o reabrir la pestaña (sessionStorage se limpia al cerrarla).
const DISMISS_KEY = 'webBleBadgeDismissed';

// Glyph de impresora monocromo inline (reemplaza el emoji nativo 🖨️, regla DESIGN.md).
// El color lo hereda del prop `color` para alinearse con el estado (verde lista / azul
// por-confirmar).
const PrinterGlyph = ({ color }: { color: string }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const readDismissed = (): boolean => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    }
  } catch {
    /* sessionStorage no disponible (tests/SSR) */
  }
  return false;
};

const writeDismissed = () => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem(DISMISS_KEY, '1');
    }
  } catch {
    /* no-op */
  }
};

const clearDismissed = () => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem(DISMISS_KEY);
    }
  } catch {
    /* no-op */
  }
};

export const WebBleStatusBadge = () => {
  const navigate = useNavigate();
  const { status, pairedCount, connectedCount } = useWebBleReady();
  const [dismissed, setDismissed] = useState(readDismissed);

  // Si TODO vuelve a estar conectado, olvidamos el descarte: así, si más tarde se
  // desconecta de nuevo, la barra "por confirmar" vuelve a aparecer (no se queda muda).
  useEffect(() => {
    if (status === 'ready') {
      clearDismissed();
      setDismissed(false);
    }
  }, [status]);

  if (status === 'off' || status === 'unsupported') return null;

  // Estado positivo: todo conectado → píldora verde compacta, no intrusiva.
  if (status === 'ready') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '6px 12px',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            borderRadius: 999,
            border: '1px solid rgba(8,195,128,0.3)',
            background: 'rgba(8,195,128,0.1)',
            color: '#04AF00',
            padding: '3px 10px',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          <PrinterGlyph color="#04AF00" />
          <span>Impresora lista</span>
        </span>
      </div>
    );
  }

  // Estado por confirmar: al menos una emparejada+habilitada sin conexión.
  // Descartable por sesión: si el cajero ya la cerró, no insistimos hasta recargar.
  if (dismissed) return null;

  const pendingCount = pairedCount - connectedCount;
  const message =
    pendingCount > 1
      ? `Confirma tus impresoras Bluetooth (${pendingCount} por confirmar) — toca aquí`
      : 'Confirma tu impresora Bluetooth para dejarla lista — toca aquí';

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderBottom: '1px solid #CDE6EE',
        background: '#F4FBFC',
      }}
    >
      <button
        type="button"
        onClick={() => navigate(PRINTING_DEEP_LINK)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          flex: 1,
          border: 0,
          background: 'transparent',
          color: '#327A95',
          padding: '7px 12px',
          fontSize: 12.5,
          fontWeight: 600,
          cursor: 'pointer',
          textAlign: 'center',
        }}
      >
        <PrinterGlyph color="#327A95" />
        <span>{message}</span>
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Ocultar aviso"
        title="Ocultar por ahora"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 28,
          height: 28,
          marginRight: 6,
          border: 0,
          background: 'transparent',
          color: '#327A95',
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  );
};
